import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Maximize2, X } from 'lucide-react';

/**
 * BeforeAfterSlider — premium drag-to-reveal handle (Awwwards-grade).
 *
 *  Props:
 *    before  : { src, srcSet?, label }   raw input
 *    after   : { src, srcSet?, label }   MODARIA result
 *    initial : 0..1     where the divider starts (default 0.5)
 *    aspect  : "2/3" | "3/4" | "1/1" | "4/5"   (default "3/4")
 *    locale  : "es" | "en"
 *
 *  Uses CSS `aspect-ratio` (works in fullscreen too — no padding-top hack
 *  that collapses when width:auto).
 */

export default function BeforeAfterSlider({
  before,
  after,
  initial = 0.5,
  aspect = '3/4',
  locale = 'es',
  className = '',
  testId = 'before-after-slider',
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(initial);
  const [dragging, setDragging] = useState(false);
  const [hinted, setHinted] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    setPos(Math.min(1, Math.max(0, x)));
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setHinted(false);
    updateFromClientX(e.clientX ?? e.touches?.[0]?.clientX ?? 0);
    if (e.target.setPointerCapture && e.pointerId != null) {
      try { e.target.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    updateFromClientX(e.clientX ?? e.touches?.[0]?.clientX ?? 0);
  };
  const onPointerUp = () => setDragging(false);

  const onClick = (e) => {
    if (dragging) return;
    setHinted(false);
    updateFromClientX(e.clientX);
  };

  const onKeyDown = (e) => {
    const big = e.metaKey || e.ctrlKey;
    if (e.key === 'ArrowLeft')      setPos((p) => Math.max(0, p - (big ? 0.12 : 0.04)));
    else if (e.key === 'ArrowRight') setPos((p) => Math.min(1, p + (big ? 0.12 : 0.04)));
    else if (e.key === 'Home')       setPos(0);
    else if (e.key === 'End')        setPos(1);
    else if (e.key === 'f' || e.key === 'F') setFullscreen((v) => !v);
    else return;
    e.preventDefault();
    setHinted(false);
  };

  // ESC closes fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [fullscreen]);

  const padding = aspect.replace('/', ' / ');  // CSS aspect-ratio value
  const beforeLabel = before?.label || (locale === 'es' ? 'INPUT' : 'INPUT');
  const afterLabel  = after?.label  || (locale === 'es' ? 'MODARIA' : 'MODARIA');

  const stage = (
    <div
      ref={containerRef}
      tabIndex={0}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos * 100)}
      aria-label={locale === 'es' ? 'Comparador antes y después' : 'Before and after comparator'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={`relative w-full h-full select-none overflow-hidden bg-bg-secondary outline-none focus-visible:ring-1 focus-visible:ring-acid ${dragging ? 'cursor-ew-resize' : 'cursor-col-resize'} ${className}`}
      style={{ aspectRatio: padding, touchAction: 'none' }}
      data-testid={testId}
    >
      {/* AFTER (full underlay) — object-contain so the FULL editorial image is visible.
          Letterboxed against bg-bg-secondary for a documentary, gallery feel. */}
      <img loading="lazy" decoding="async"
        src={after.src}
        srcSet={after.srcSet}
        alt={after.alt || afterLabel}
        draggable="false"
        className="absolute inset-0 w-full h-full object-contain"
        data-testid={`${testId}-after-image`}/>

      {/* BEFORE clipped to the left of the divider. Critical: the BEFORE image is
          rendered into a container whose width matches the FULL stage so its
          proportions stay identical to the AFTER. We then clip horizontally. */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos * 100}%`, willChange: 'width' }}
        data-testid={`${testId}-before-clip`}
      >
        <div
          className="absolute inset-0 bg-bg-secondary"
          style={{ width: `${100 / Math.max(pos, 0.001)}%` }}
        >
          <img loading="lazy" decoding="async"
            src={before.src}
            srcSet={before.srcSet}
            alt={before.alt || beforeLabel}
            draggable="false"
            className="absolute inset-0 w-full h-full object-contain"
            data-testid={`${testId}-before-image`}/>
        </div>
      </div>

      {/* Vertical divider line */}
      <div
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{ left: `${pos * 100}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-0 bottom-0 w-px bg-acid/90 shadow-[0_0_18px_rgba(232,255,58,0.45)]" />
      </div>

      {/* Drag knob */}
      <div
        className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ left: `${pos * 100}%`, transform: `translate(-50%, -50%)` }}
      >
        <motion.div
          animate={{ scale: dragging ? 1.15 : 1 }}
          transition={{ duration: 0.18 }}
          className="w-12 h-12 rounded-full border border-acid bg-bg/40 backdrop-blur-md flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
        >
          <span className="block w-3 h-px bg-acid" />
          <span className="mx-1 block w-1.5 h-1.5 rounded-full bg-acid" />
          <span className="block w-3 h-px bg-acid" />
        </motion.div>
      </div>

      {/* Corner pills */}
      <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink bg-bg/70 backdrop-blur-sm border border-line-strong px-2 py-1">
        {beforeLabel}
      </div>
      <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.22em] text-bg bg-acid px-2 py-1">
        {afterLabel}
      </div>

      {/* Fullscreen toggle */}
      {!fullscreen && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setFullscreen(true); }}
          aria-label={locale === 'es' ? 'Pantalla completa' : 'Fullscreen'}
          data-testid={`${testId}-fullscreen-btn`}
          className="absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center bg-bg/70 backdrop-blur-sm border border-line-strong text-ink hover:text-acid hover:border-acid transition-colors"
        >
          <Maximize2 size={13} />
        </button>
      )}

      {/* Drag hint (fades on first interaction) */}
      {hinted && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted bg-bg/70 backdrop-blur-sm border border-line px-2 py-1 pointer-events-none"
          data-testid={`${testId}-hint`}
        >
          {locale === 'es' ? '← ARRASTRA →' : '← DRAG →'}
        </motion.div>
      )}
    </div>
  );

  if (!fullscreen) return stage;

  return (
    <>
      {/* Original keeps its layout (avoid collapse during portal) */}
      <div className="relative w-full bg-bg-secondary/40 border border-dashed border-line" style={{ aspectRatio: padding }} aria-hidden />

      {/* Lightbox — uses aspect-ratio + viewport-relative max sizing so the
          stage actually paints (the old paddingTop hack collapsed at width:auto). */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-bg/95 backdrop-blur-sm flex items-center justify-center p-6"
        data-testid={`${testId}-fullscreen`}
      >
        <button
          type="button"
          onClick={() => setFullscreen(false)}
          aria-label={locale === 'es' ? 'Cerrar' : 'Close'}
          className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center border border-line-strong bg-bg/60 backdrop-blur-sm text-ink hover:text-acid hover:border-acid transition-colors"
          data-testid={`${testId}-fullscreen-close`}
        >
          <X size={16} />
        </button>
        <div
          className="relative"
          style={{
            aspectRatio: padding,
            height: 'min(92vh, calc(95vw * (var(--ar-h, 4) / var(--ar-w, 3))))',
            width: 'auto',
            maxHeight: '92vh',
            maxWidth: '95vw',
            // CSS vars used above so the math respects the slider aspect dynamically.
            '--ar-w': aspect.split('/')[0],
            '--ar-h': aspect.split('/')[1],
          }}
        >
          {stage}
        </div>
      </motion.div>
    </>
  );
}
